import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Lock, ArrowLeft, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { Button, Input, Card, Spinner } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { verifyPasswordResetCode, confirmPasswordReset } from '@perfin/shared';

const PASSWORD_REQUIREMENTS = [
  'At least 12 characters',
  'One uppercase letter (A-Z)',
  'One lowercase letter (a-z)',
  'One number (0-9)',
  'One special character (!@#$%^&*...)',
];

function PasswordInfoIcon() {
  return (
    <div className="relative group inline-block ml-1">
      <Info className="h-4 w-4 text-gray-400 cursor-help" />
      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg whitespace-nowrap">
          <p className="font-semibold mb-1">Password requirements:</p>
          <ul className="space-y-0.5">
            {PASSWORD_REQUIREMENTS.map((req, i) => (
              <li key={i}>• {req}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

type ActionMode = 'resetPassword' | 'verifyEmail' | 'recoverEmail';
type PageState = 'loading' | 'ready' | 'success' | 'error';

export function AuthActionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const mode = searchParams.get('mode') as ActionMode | null;
  const oobCode = searchParams.get('oobCode');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [email, setEmail] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>();

  const password = watch('password');

  // Verify the oobCode on mount
  useEffect(() => {
    async function verifyCode() {
      if (!oobCode || mode !== 'resetPassword') {
        setErrorMessage('Invalid or missing reset link.');
        setPageState('error');
        return;
      }

      try {
        const userEmail = await verifyPasswordResetCode(oobCode);
        setEmail(userEmail);
        setPageState('ready');
      } catch (error) {
        console.error('Invalid reset code:', error);
        setErrorMessage('This password reset link is invalid or has expired.');
        setPageState('error');
      }
    }

    verifyCode();
  }, [oobCode, mode]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!oobCode) return;

    setSubmitting(true);
    try {
      await confirmPasswordReset(oobCode, data.password);
      setPageState('success');
      toast.success('Password reset successfully!');
    } catch (error) {
      console.error('Password reset failed:', error);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <Spinner size="lg" />
          <p className="text-gray-600 mt-4">Verifying reset link...</p>
        </div>
      </Card>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <Card padding="lg">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Invalid</h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <Link to="/forgot-password">
            <Button className="w-full">Request New Reset Link</Button>
          </Link>
          <Link to="/login" className="block mt-4">
            <Button variant="secondary" className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to sign in
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <Card padding="lg">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Reset Complete</h2>
          <p className="text-gray-600 mb-6">
            Your password has been reset successfully. You can now sign in with your new password.
          </p>
          <Button className="w-full" onClick={() => navigate('/login')}>
            Sign in
          </Button>
        </div>
      </Card>
    );
  }

  // Ready state - show reset form
  return (
    <Card padding="lg">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Set new password</h2>
        <p className="text-gray-600 mt-1">
          Enter a new password for <span className="font-medium">{email}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label={
            <span className="flex items-center">
              New Password
              <PasswordInfoIcon />
            </span>
          }
          type="password"
          placeholder="••••••••••••"
          leftIcon={<Lock className="h-4 w-4" />}
          error={errors.password?.message}
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 12,
              message: 'Password must be at least 12 characters',
            },
            pattern: {
              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
              message: 'Password must contain uppercase, lowercase, number, and special character',
            },
          })}
        />

        <Input
          label="Confirm Password"
          type="password"
          placeholder="Confirm new password"
          leftIcon={<Lock className="h-4 w-4" />}
          error={errors.confirmPassword?.message}
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: (value) => value === password || 'Passwords do not match',
          })}
        />

        <Button type="submit" className="w-full" loading={submitting}>
          Reset Password
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600 mt-6">
        Remember your password?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          Sign in
        </Link>
      </p>
    </Card>
  );
}
